import { Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { ACCENT_COLORS, type AccentColorKey } from '@/theme';
import type { UserAccountType } from '@/core/userAccountType';

/**
 * The accent whose tone each account type carries. Named palette entries rather
 * than literal hexes, so the types follow the project's colours if those move —
 * and deliberately not MUI's semantic `success`/`warning`, which would read as
 * a judgement on the account rather than a classification of it.
 */
const ACCENT_BY_TYPE: Record<UserAccountType, AccentColorKey> = {
  Local: 'green',
  Entra: 'purple',
  // Nexo turquoise (#00C8B4) — the house accent the shell already wears on the
  // selected module icon. Support is our own people, not the customer's, and
  // the brand tone is what says so.
  Support: 'teal',
  // Red, because a break-glass account is the exception the SSO design governs
  // most tightly (D5) — an admin scanning the list should stop on it.
  BreakGlass: 'red',
};

interface UserAccountTypeChipProps {
  accountType: UserAccountType;
}

/** One chip saying how this account signs in. See `userAccountType.ts` for the value. */
export function UserAccountTypeChip({ accountType }: UserAccountTypeChipProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  const accent = ACCENT_COLORS[ACCENT_BY_TYPE[accountType]];
  // `darkMain` where the accent defines one, matching how the theme picks its
  // primary tone: the darker brand tones are illegible on the dark shell.
  const background = theme.palette.mode === 'dark' ? accent.darkMain ?? accent.main : accent.main;

  return (
    <Chip
      label={t(`identity.accountType.${accountType}`)}
      size="small"
      sx={{
        backgroundColor: background,
        // getContrastText rather than a fixed white: the amber and green tones
        // are light enough that white label text falls below readable contrast.
        color: theme.palette.getContrastText(background),
        fontWeight: 500,
      }}
    />
  );
}
